import React, { useEffect, useState, useRef, useLayoutEffect, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SendIcon, PaperclipIcon, ImageIcon, FileIcon, XIcon, ChevronLeftIcon, UserIcon, PhoneIcon, VideoIcon, InfoIcon, MessageSquareIcon, MoreVerticalIcon, ArchiveIcon, ArchiveRestoreIcon, InboxIcon, LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { Conversation, Message, getUserConversations, subscribeToUserConversations, subscribeToConversationMessages, sendMessage, markConversationOpened, createConversation, getTeacherById, getConversation, archiveConversation, unarchiveConversation } from '../../services/messagingService';
import { getPublicTeacherProfile } from '../../services/publicProfileService';
// Optimistic message extends Message with UI-only flags
type OptimisticMessage = Message & {
  pending?: boolean;
  failed?: boolean;
  // Stamped after sendMessage() resolves with the real Firestore message id.
  // The snapshot dedup uses this as the primary match key so we no longer
  // rely solely on content (senderId + text + attachmentName) to know an
  // optimistic bubble has been confirmed by the server.
  serverMessageId?: string;
};
export const StudentMessages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    loading: authLoading
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  // Track which conversation IDs have been confirmed by a server-side
  // read (either the conversations subscription or a direct fetch). This
  // is the gate that prevents the message subscription from attaching to
  // a freshly-created conversation BEFORE its document has fully
  // replicated server-side. Without this gate, attaching the message
  // listener against an unconfirmed doc can hit a transient permission
  // race in Firestore's rules engine — the listener silently errors out
  // and never recovers, leaving the open thread frozen until the user
  // manually reselects the conversation. Once an ID lands in this set
  // it stays there for the session (we never remove).
  const [confirmedConvoIds, setConfirmedConvoIds] = useState<Set<string>>(() => new Set());
  // Generation counter that forces the active-thread message subscription
  // to tear down and re-attach. Bumped by the self-healing watcher below
  // whenever the conversations subscription reports a `lastMessageTimestamp`
  // newer than anything in our locally-held `messages` — proof that the
  // current message listener has silently fallen behind (the failure mode
  // that strands a teacher's first reply in a brand-new conversation).
  const [messageSubGeneration, setMessageSubGeneration] = useState(0);
  // Tracks the most recent `lastMessageTimestamp` we have already triggered
  // a re-subscription for. Prevents the self-healing watcher from looping
  // if the freshly-attached listener also fails — we will retry at most
  // once per advancing server timestamp.
  const lastResyncedTimestampRef = useRef<number>(0);
  // Resolved public-profile identity for each teacher the student is
  // conversing with. The conversation document only stores a snapshot of
  // `teacherName` / `instrument` taken at conversation-creation time —
  // which is often the generic literal "Teacher" / "Music" fallback. To
  // show the teacher's real public display name (matching what the
  // student sees in the dashboard, schedule, and Find Teachers surfaces),
  // we read `publicTeacherProfiles/{teacherId}` for each unique teacherId
  // in the conversation list and cache the result here. The render path
  // prefers this value and falls back to the stored conversation snapshot
  // only when the public profile is unavailable.
  const [teacherProfilesMap, setTeacherProfilesMap] = useState<Map<string, {
    displayName: string;
    instrument: string;
    avatarUrl: string;
  }>>(() => new Map());
  // Track which teacherIds we've already attempted to fetch so we don't
  // re-fire the read on every conversations subscription tick. We retain
  // even failed lookups in the set — a transient permission error during
  // initial auth bootstrap shouldn't cause endless retries on every snapshot.
  const fetchedTeacherIdsRef = useRef<Set<string>>(new Set());
  // Phase 2/3: Track which deep-link openConversationId we've already
  // honored so we don't re-open the conversation after the user navigates
  // away to a different one.
  const handledDeepLinkRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  // Optimistic messages: instant bubble for an in-flight send/upload.
  // Atomically deduped in the snapshot handler (same render cycle as setMessages)
  // to avoid the jitter that caused the prior optimistic system to be removed.
  const [optimisticMessages, setOptimisticMessages] = useState<OptimisticMessage[]>([]);
  const optimisticObjectUrlsRef = useRef<Map<string, string>>(new Map());
  // Note: Optimistic-message architecture was removed to match the smoother
  // teacher-side flow. Messages are now server-authoritative: send → await
  // Firestore → snapshot delivers the bubble. One DOM mount per message,
  // no temp/server identity swap, no merge/dedup logic in the snapshot
  // reducer. This is what makes TeacherMessages feel jitter-free.
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Track whether messages are loading for the selected conversation (prevents empty-state flash).
  // Initialize TRUE so the right panel doesn't briefly render the "Start a conversation" empty
  // state during the first paint after auto-selecting a conversation. Without this, the panel
  // visibly flashes empty-state → loading → messages (the "double-jump" regression).
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  // Lightbox state for image viewer
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isSending, setIsSending] = useState(false);
  // NEW: Track user scroll position to avoid interrupting manual scrolling
  const userScrollPositionRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
    isNearBottom: boolean;
    hasManuallyScrolled: boolean;
  }>({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    isNearBottom: true,
    hasManuallyScrolled: false
  });
  // Flag to track if we should force scroll after sending
  const shouldForceScrollRef = useRef(false);
  // NEW: Use a single scroll operation ID to prevent race conditions
  const scrollOperationIdRef = useRef(0);
  // Track last message count to determine if new messages arrived
  const lastMessageCountRef = useRef(0);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Check for teacherId in query params (for direct messaging from teacher profile)
  const queryParams = new URLSearchParams(location.search);
  const teacherIdParam = queryParams.get('teacherId');
  // NEW: Better mobile state tracking
  const [mobileState, setMobileState] = useState({
    isKeyboardOpen: false,
    isTyping: false,
    viewportHeight: window.innerHeight // Track viewport height for keyboard detection
  });
  // Archive UI state (student-side only)
  const [archivedView, setArchivedView] = useState(false);
  const [menuOpenForId, setMenuOpenForId] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  // Phase 2/3: Deep-link to a specific conversation from the student bell.
  // When StudentNotificationBell navigates here with
  // `state.openConversationId`, find and select that conversation as soon
  // as the conversation list contains it.
  useEffect(() => {
    const targetId = (location.state as any)?.openConversationId as string | undefined;
    if (!targetId) return;
    if (handledDeepLinkRef.current === targetId) return;
    if (!conversations || conversations.length === 0) return;
    const match = conversations.find(c => c.id === targetId);
    if (match) {
      setSelectedConversation(match);
      if (isMobileView) {
        setShowConversationList(false);
      }
      handledDeepLinkRef.current = targetId;
    }
  }, [location.state, conversations, isMobileView]);
  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      // NEW: Update viewport height on resize
      if (mobile) {
        setMobileState(prev => ({
          ...prev,
          viewportHeight: window.innerHeight
        }));
      }
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Set mounted ref for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Handle direct teacher contact from profile page
  const handleTeacherContact = async (teacherId: string) => {
    try {
      console.log('Starting teacher contact process with teacherId:', teacherId);
      // Check if we already have a conversation with this teacher
      const existingConversation = conversations.find(conv => conv.teacherId === teacherId);
      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation.id);
        // Use existing conversation
        handleSelectConversation(existingConversation);
      } else {
        console.log('No existing conversation found, creating new one');
        // Create a new conversation
        const teacher = await getTeacherById(teacherId);
        if (!teacher) {
          console.error('Teacher not found with ID:', teacherId);
          setError('Teacher not found');
          return;
        }
        console.log('Teacher data retrieved:', teacher.fullName);
        // Create conversation with proper fallbacks for all required fields
        const conversationId = await createConversation(teacherId, teacher.fullName || 'Teacher', teacher.profilePictureUrl || '', currentUser.uid, currentUser.displayName || 'Student', currentUser.photoURL || '', teacher.instruments && teacher.instruments.length > 0 ? teacher.instruments[0] : 'Music');
        console.log('New conversation created with ID:', conversationId);
        // Refresh conversations to include the new one
        const updatedConversations = await getUserConversations(currentUser.uid, 'student');
        setConversations(updatedConversations);
        // Find and select the new conversation
        const newConversation = updatedConversations.find(conv => conv.id === conversationId);
        if (newConversation) {
          console.log('Found and selecting new conversation');
          handleSelectConversation(newConversation);
        } else {
          console.error('Created conversation not found in updated list');
          try {
            // Force a direct fetch of the conversation to handle any timing issues
            const directFetchedConversation = await getConversation(conversationId);
            if (directFetchedConversation) {
              setConversations([...updatedConversations, directFetchedConversation]);
              handleSelectConversation(directFetchedConversation);
            }
          } catch (fetchErr) {
            console.error('Error fetching new conversation:', fetchErr);
            // Handle the error gracefully without crashing the component
            setError('Error loading conversation. Please try refreshing the page.');
          }
        }
      }
      // On mobile, explicitly hide the conversation list to show the message area
      if (isMobileView) {
        setShowConversationList(false);
      }
    } catch (err) {
      console.error('Error handling teacher contact:', err);
      setError('Failed to start conversation with teacher. Please try again.');
    }
  };
  // Fetch conversations on component mount
  useEffect(() => {
    // Wait for Firebase Auth to finish restoring session before checking auth
    if (authLoading) return;
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    const fetchConversations = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        console.log('Fetching conversations for user:', currentUser.uid);
        // Verify user object has required properties
        if (!currentUser.uid) {
          console.error('Current user missing UID');
          setError('Authentication error. Please try logging in again.');
          setLoading(false);
          return;
        }
        // Get conversations first
        const userConversations = await getUserConversations(currentUser.uid, 'student');
        console.log('Fetched conversations:', userConversations.length);
        // Filter out invalid conversations
        const validConversations = userConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
        if (validConversations.length < userConversations.length) {
          console.warn(`Filtered out ${userConversations.length - validConversations.length} invalid conversations`);
        }
        // IMPROVED: Make sure conversations are sorted by lastMessageTimestamp (newest first)
        const sortedConversations = sortConversationsByTimestamp(validConversations);
        // Always set conversations (even if empty) and clear loading state
        if (isMountedRef.current) {
          setConversations(sortedConversations);
          setLoading(false);
          // Auto-select first conversation on desktop if none selected
          if (!isMobileView && sortedConversations.length > 0 && !selectedConversation) {
            setSelectedConversation(sortedConversations[0]);
            activeConversationIdRef.current = sortedConversations[0].id;
          }
        }
        // If there's a teacherId in the URL, handle it after conversations are loaded
        if (teacherIdParam) {
          console.log('Found teacherId in URL params:', teacherIdParam);
          handleTeacherContact(teacherIdParam);
        }
        // RETRY: If initial fetch returned empty, retry once after a delay
        // This handles the case where the auth token hasn't fully propagated yet
        if (sortedConversations.length === 0 && isMountedRef.current) {
          console.log('[Messaging] Initial fetch returned empty, scheduling retry in 1.5s...');
          retryTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;
            try {
              console.log('[Messaging] Retrying conversation fetch...');
              const retryConversations = await getUserConversations(currentUser.uid, 'student');
              const validRetry = retryConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
              const sortedRetry = sortConversationsByTimestamp(validRetry);
              if (isMountedRef.current && sortedRetry.length > 0) {
                console.log(`[Messaging] Retry succeeded: ${sortedRetry.length} conversations`);
                setConversations(sortedRetry);
                // Auto-select first conversation on desktop if none selected
                if (!isMobileView && !selectedConversation) {
                  setSelectedConversation(sortedRetry[0]);
                  activeConversationIdRef.current = sortedRetry[0].id;
                }
              } else {
                console.log('[Messaging] Retry also returned empty');
              }
            } catch (retryErr) {
              console.warn('[Messaging] Retry fetch failed:', retryErr);
            }
          }, 1500);
        }
      } catch (err) {
        console.error('Error fetching conversations:', err);
        // More detailed error logging
        if (err instanceof Error) {
          console.error('Error details:', err.message);
          console.error('Error stack:', err.stack);
        }
        if (isMountedRef.current) {
          setError('Failed to load conversations. Please try again.');
          setLoading(false); // Make sure to set loading to false even on error
          // RETRY on error: schedule a retry after delay
          console.log('[Messaging] Initial fetch errored, scheduling retry in 2s...');
          retryTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;
            try {
              const retryConversations = await getUserConversations(currentUser.uid, 'student');
              const validRetry = retryConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
              const sortedRetry = sortConversationsByTimestamp(validRetry);
              if (isMountedRef.current && sortedRetry.length > 0) {
                setConversations(sortedRetry);
                setError('');
                // Auto-select first conversation on desktop if none selected
                if (!isMobileView && !selectedConversation) {
                  setSelectedConversation(sortedRetry[0]);
                  activeConversationIdRef.current = sortedRetry[0].id;
                }
              }
            } catch (retryErr) {
              console.warn('[Messaging] Retry fetch failed:', retryErr);
            }
          }, 2000);
        }
      }
    };
    fetchConversations();
    // Set up subscription after initial fetch
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = subscribeToUserConversations(currentUser.uid, fetchedConversations => {
        console.log('Subscription update - conversations:', fetchedConversations.length);
        // Filter out invalid conversations
        const validConversations = fetchedConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
        // IMPROVED: Make sure conversations are sorted by lastMessageTimestamp (newest first)
        const sortedConversations = sortConversationsByTimestamp(validConversations);
        if (isMountedRef.current) {
          // Always update with whatever data the subscription provides
          // If subscription returns data, use it; if empty, only keep prev if prev has data
          setConversations(prev => sortedConversations.length > 0 ? sortedConversations : prev.length > 0 ? prev : sortedConversations);
          // Auto-select first conversation on desktop if none selected.
          //
          // IMPORTANT: Read the "is something selected" answer from the ref,
          // not from `selectedConversation` state. This callback is defined
          // inside an effect whose deps do NOT include `selectedConversation`,
          // so the closure variable is frozen at its mount-time value (null).
          // Using the closure caused this branch to fire on EVERY conversation
          // update — including when the teacher sent a reply — which spammed
          // setSelectedConversation with new object references and disrupted
          // the active-thread subscription guard. The ref is always current,
          // so the auto-select only runs when nothing is actually selected.
          if (!isMobileView && sortedConversations.length > 0 && !activeConversationIdRef.current) {
            setSelectedConversation(sortedConversations[0]);
            activeConversationIdRef.current = sortedConversations[0].id;
          }
          // Clear any error if we successfully got an update with data
          if (sortedConversations.length > 0 && error) setError('');
          // Ensure loading is set to false on subscription update
          if (loading) setLoading(false);
        }
      }, 'student');
    } catch (subErr) {
      console.error('Error setting up conversation subscription:', subErr);
      // Set loading to false if subscription fails
      if (isMountedRef.current && loading) {
        setLoading(false);
      }
    }
    // Safety timeout to ensure loading state doesn't get stuck
    const safetyTimeout = setTimeout(() => {
      if (isMountedRef.current && loading) {
        console.log('Safety timeout triggered - forcing loading state to false');
        setLoading(false);
      }
    }, 5000); // 5 second safety timeout
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
      clearTimeout(safetyTimeout);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [currentUser, authLoading, navigate, teacherIdParam]); // Removed error and loading from dependencies
  // NEW: Helper function to sort conversations by timestamp
  const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => {
      // Get timestamp values, properly handling Firestore timestamps
      const getTimestampValue = (timestamp: any): number => {
        // For Firestore timestamps with toDate method
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime();
          } catch (e) {
            console.error('Error converting Firestore timestamp:', e);
            return 0;
          }
        }
        // For Date objects
        if (timestamp instanceof Date) {
          return timestamp.getTime();
        }
        // For numeric timestamps
        if (typeof timestamp === 'number') {
          return timestamp;
        }
        // Default case - use 0 to ensure consistent sorting
        return 0;
      };
      const timeA = getTimestampValue(a.lastMessageTimestamp);
      const timeB = getTimestampValue(b.lastMessageTimestamp);
      // Sort in descending order (newest first)
      return timeB - timeA;
    });
  };
  // Sync confirmedConvoIds whenever the conversations list updates. Every
  // conversation that appears in `conversations` has been observed by a
  // server-side read, so its document is committed and the messages
  // subscription is now safe to attach against it. We additively merge
  // (never remove) so a conversation that briefly disappears from the
  // visible bucket (e.g. pending serverTimestamp resolution) does not
  // cause the messages listener to tear down.
  useEffect(() => {
    if (conversations.length === 0) return;
    setConfirmedConvoIds(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const c of conversations) {
        if (c.id && !next.has(c.id)) {
          next.add(c.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [conversations]);
  // Resolve real public display names for every teacher in the conversation
  // list. Reads `publicTeacherProfiles/{teacherId}` (the same source the
  // student dashboard and schedule surfaces use), so the messages page can't
  // get out of sync with the rest of the student app. We additively merge —
  // existing entries are kept on subsequent ticks and only NEW teacherIds
  // trigger a network read.
  useEffect(() => {
    if (conversations.length === 0) return;
    const toFetch: string[] = [];
    for (const c of conversations) {
      if (!c.teacherId) continue;
      if (fetchedTeacherIdsRef.current.has(c.teacherId)) continue;
      fetchedTeacherIdsRef.current.add(c.teacherId);
      toFetch.push(c.teacherId);
    }
    if (toFetch.length === 0) return;
    let cancelled = false;
    Promise.all(toFetch.map(async teacherId => {
      try {
        const profile = await getPublicTeacherProfile(teacherId);
        if (!profile) return null;
        const fullName = typeof profile.displayName === 'string' && profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || '';
        const instruments = Array.isArray(profile.instruments) ? profile.instruments : [];
        const primaryInstrument = typeof instruments[0] === 'string' ? instruments[0] : '';
        const avatarUrl = typeof profile.profileImageUrl === 'string' && profile.profileImageUrl || typeof profile.profilePictureUrl === 'string' && profile.profilePictureUrl || '';
        return {
          teacherId,
          displayName: fullName,
          instrument: primaryInstrument,
          avatarUrl
        };
      } catch (err) {
        console.warn('[StudentMessages] Failed to resolve public profile for teacher', teacherId, err);
        return null;
      }
    })).then(results => {
      if (cancelled) return;
      const valid = results.filter((r): r is NonNullable<typeof r> => r !== null);
      if (valid.length === 0) return;
      setTeacherProfilesMap(prev => {
        const next = new Map(prev);
        for (const r of valid) {
          next.set(r.teacherId, {
            displayName: r.displayName,
            instrument: r.instrument,
            avatarUrl: r.avatarUrl
          });
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [conversations]);
  // Resolver helpers: pick the best available identity for a teacher.
  // Always prefer the public profile (real, current name) over the stored
  // conversation snapshot (often the literal "Teacher" / "Music" fallback
  // recorded when the conversation was created from the teacher's profile
  // page). All visible teacher identity on the student messages page must
  // route through these helpers — never read `conversation.teacherName`
  // directly, or the generic label will leak back into the UI.
  const resolveTeacherDisplayName = (conv: Conversation): string => {
    const fromProfile = conv.teacherId ? teacherProfilesMap.get(conv.teacherId)?.displayName : '';
    if (fromProfile && fromProfile.trim()) return fromProfile.trim();
    const stored = (conv.teacherName || '').trim();
    // Reject the generic placeholder so we don't show "Teacher" as a name —
    // better to wait for the profile fetch to complete than to display the
    // confusing fallback when the student has multiple conversations.
    if (stored && stored.toLowerCase() !== 'teacher') return stored;
    return 'Teacher';
  };
  const resolveTeacherSubtitle = (conv: Conversation): string => {
    const fromProfile = conv.teacherId ? teacherProfilesMap.get(conv.teacherId)?.instrument : '';
    const instrument = fromProfile && fromProfile.trim() || (conv.instrument || '').trim();
    // Suppress the bare "Music Teacher" subtitle that the createConversation
    // path writes when no real instrument is on the teacher's profile yet.
    if (!instrument || instrument.toLowerCase() === 'music') return 'Teacher';
    return `${instrument} Teacher`;
  };
  const resolveTeacherAvatar = (conv: Conversation): string => {
    const fromProfile = conv.teacherId ? teacherProfilesMap.get(conv.teacherId)?.avatarUrl : '';
    if (fromProfile) return fromProfile;
    return conv.teacherAvatar || '';
  };
  // Self-healing watcher: detect when the active-thread message subscription
  // has silently fallen behind the conversations subscription.
  //
  // Failure mode this catches (the bug we are fixing):
  //   1. Student creates a brand-new conversation and sends the first message.
  //      The local Firestore cache renders the student's own bubble instantly
  //      via latency compensation, so the panel APPEARS healthy.
  //   2. The server-side rules engine, however, was still propagating the
  //      newly-created conversation doc when the message listener attached.
  //      The listener errors out on the server and Firestore terminates it.
  //      Logging is the only recovery path messagingService.ts offers — no
  //      retry is built in.
  //   3. Teacher replies. The conversations listener (set up earlier and on
  //      a stable doc) picks up the conversation's updated `lastMessage` and
  //      `lastMessageTimestamp` — that is why the LEFT card updates with
  //      "Hello" and shows an unread badge.
  //   4. But the messages listener is dead. The teacher's reply never lands
  //      in `messages`, so the RIGHT panel stays frozen on the student's
  //      outgoing bubble until the student manually reselects the conversation
  //      (which mounts a fresh listener against a now-fully-replicated doc).
  //
  // The heal: when the conversations subscription reports a server
  // `lastMessageTimestamp` for the active conversation that is meaningfully
  // newer than the newest timestamp present in our local `messages`, we know
  // the listener has missed at least one write. Bump `messageSubGeneration`
  // to force the message subscription effect below to tear down and
  // re-attach. The fresh listener attaches against a now-replicated doc and
  // delivers the missed message(s) on its first snapshot.
  //
  // Loop guard: `lastResyncedTimestampRef` records which advancing timestamp
  // we have already healed against, so a re-attached-but-still-broken listener
  // can only trigger ONE resync per new server timestamp (not a tight loop).
  useEffect(() => {
    if (!selectedConversation?.id) return;
    const live = conversations.find(c => c.id === selectedConversation.id);
    if (!live || !live.lastMessageTimestamp) return;
    const liveTs = getTimestampValue(live.lastMessageTimestamp);
    if (!liveTs) return;
    const localLatest = messages.length > 0 ? messages.reduce((max, m) => Math.max(max, getTimestampValue(m.timestamp)), 0) : 0;
    // 1500ms grace to absorb clock skew between client-stamped optimistic
    // bubbles and Firestore's serverTimestamp resolution on the conversation
    // doc. Below this delta we assume the listener is in sync.
    const DRIFT_GRACE_MS = 1500;
    if (liveTs > localLatest + DRIFT_GRACE_MS && liveTs > lastResyncedTimestampRef.current) {
      console.log('[StudentMessages] Active-thread listener appears stale — forcing re-subscribe', {
        conversationId: selectedConversation.id,
        liveTs,
        localLatest
      });
      lastResyncedTimestampRef.current = liveTs;
      setMessageSubGeneration(g => g + 1);
    }
  }, [conversations, selectedConversation?.id, messages]);
  // Reset the resync guard when the active conversation changes so the
  // watcher starts fresh for the newly-opened thread.
  useEffect(() => {
    lastResyncedTimestampRef.current = 0;
  }, [selectedConversation?.id]);
  // Subscribe to messages when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      activeConversationIdRef.current = null;
      return;
    }
    // Validate the selected conversation has all required fields
    if (!selectedConversation.id || !selectedConversation.teacherId) {
      console.error('Selected conversation is missing critical fields:', selectedConversation);
      setError('Invalid conversation data. Please try selecting another conversation.');
      return;
    }
    // CRITICAL: Wait until the conversation has been confirmed via a
    // server-side read before attaching the message listener.
    //
    // Failure mode without this guard (the bug we are fixing):
    //   1. Student creates a brand-new conversation from a teacher profile.
    //      `handleTeacherContact` calls `createConversation` and then
    //      immediately calls `handleSelectConversation(newConvo)` — often
    //      against a doc fetched directly by id before the conversations
    //      subscription has propagated.
    //   2. This effect fires and attaches `subscribeToConversationMessages`
    //      against the new conversation id.
    //   3. Firestore's server-side rules engine evaluates the listener's
    //      participant check against a conversation doc that has not yet
    //      finished replicating server-side. The check resolves as denied,
    //      the listener errors out, and Firestore terminates it.
    //   4. The teacher's reply lands in the subcollection seconds later but
    //      there is no live listener to deliver it — the open student thread
    //      stays frozen until the student manually reselects the
    //      conversation (which mounts a fresh listener against a now-fully-
    //      replicated doc).
    //
    // Gating on `confirmedConvoIds` fixes this: once the conversations
    // subscription has emitted a snapshot containing the new conversation,
    // we know the doc is committed and the rules engine can resolve the
    // participant check. The set is additive — IDs are never removed — so
    // a transient empty snapshot can never tear down the live listener.
    if (!confirmedConvoIds.has(selectedConversation.id)) {
      console.log('[StudentMessages] Deferring message subscription until conversation is server-confirmed:', selectedConversation.id);
      return;
    }
    // Capture the conversation id as a stable LOCAL primitive at the top of
    // this effect. The async snapshot callback below must NOT read
    // `selectedConversation.id` from React's closure — when the conversations
    // list updates (e.g. because the teacher's reply bumped lastMessage /
    // lastMessageTimestamp), the component re-renders, and during that
    // re-render the closure's `selectedConversation` reference can transiently
    // disagree with `activeConversationIdRef.current`. That mismatch causes
    // the guard below to silently DROP the incoming teacher reply, leaving the
    // open thread stuck on its previous messages until the student clicks the
    // conversation again. Comparing against this captured primitive id keeps
    // the guard correct for the full lifetime of this subscription.
    const conversationId = selectedConversation.id;
    console.log('Setting up message subscription for conversation:', conversationId);
    // Ensure the active conversation ID is set properly
    activeConversationIdRef.current = conversationId;
    setMessagesLoading(true); // Show loading state until first snapshot
    let isFirstSnapshot = true;
    // NEW: Reset scroll position tracking when conversation changes
    userScrollPositionRef.current = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      isNearBottom: true,
      hasManuallyScrolled: false
    };
    const unsubscribe = subscribeToConversationMessages(conversationId, fetchedMessages => {
      console.log(`Received ${fetchedMessages.length} messages for conversation ${conversationId}`);
      // This subscription is bound to ONE specific conversationId (captured
      // as a stable local primitive at the top of this effect). Firestore's
      // unsubscribe() in the cleanup synchronously stops callbacks for the
      // previous subscription when the active conversation changes, so every
      // snapshot delivered HERE is by definition for `conversationId`.
      //
      // We previously guarded with `activeConversationIdRef.current === conversationId`,
      // but that ref can transiently desync during React's render cycle when
      // the conversations snapshot reassigns selectedConversation to a new
      // object reference. The guard would then silently drop valid snapshots,
      // leaving:
      //  (1) the student's own optimistic "Uploading..." bubble stuck because
      //      the dedup pass below never runs, and
      //  (2) the teacher's reply invisible until the student manually re-
      //      selected the conversation.
      // Trusting the subscription lifecycle (not the ref) fixes both.
      if (isMountedRef.current) {
        // Track message count for the scroll-anchor useLayoutEffect.
        lastMessageCountRef.current = fetchedMessages.length;
        // Server-authoritative: just sort and set. No optimistic merge,
        // no content-match dedup, no temp-id swap. The scroll-anchor
        // useLayoutEffect keyed on messages.length handles the scroll
        // synchronously before paint. The reveal useLayoutEffect flips
        // messagesLoading=false on the first snapshot.
        const sortedMessages = sortMessagesByTimestamp(fetchedMessages);
        setMessages(sortedMessages);
        // Atomic dedup: in the same render cycle, drop any optimistic
        // message that the server has now confirmed (match by senderId +
        // text + attachmentName). The bubble swap is seamless.
        setOptimisticMessages(prev => {
          if (prev.length === 0) return prev;
          const remaining = prev.filter(opt => {
            const optServerId = (opt as OptimisticMessage).serverMessageId;
            const matched = sortedMessages.some(real => {
              // PRIMARY: if we already know the server id (sendMessage
              // returned it), match by stable id. This is bulletproof.
              if (optServerId && real.id === optServerId) return true;
              // FALLBACK: content match for the brief window between
              // optimistic creation and sendMessage resolving.
              return real.senderId === opt.senderId && (real.text || '') === (opt.text || '') && (real.attachmentName || '') === (opt.attachmentName || '');
            });
            if (matched) {
              const url = optimisticObjectUrlsRef.current.get(opt.id);
              if (url) {
                try {
                  URL.revokeObjectURL(url);
                } catch {}
                optimisticObjectUrlsRef.current.delete(opt.id);
              }
              return false;
            }
            return true;
          });
          return remaining.length === prev.length ? prev : remaining;
        });
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
        }
      }
    });
    // Mark messages as read
    if (currentUser?.uid) {
      markConversationOpened(selectedConversation.id, currentUser.uid, 'student').catch(err => console.error('Error marking conversation as opened:', err));
    }
    // NEW: Add scroll event listener to track user scrolling
    const messageContainer = messageContainerRef.current;
    if (messageContainer) {
      messageContainer.addEventListener('scroll', updateScrollPosition);
    }
    return () => {
      unsubscribe();
      // Reset message count when unsubscribing
      lastMessageCountRef.current = 0;
      // Remove scroll event listener
      if (messageContainer) {
        messageContainer.removeEventListener('scroll', updateScrollPosition);
      }
    };
    // Stable primitive deps (matches TeacherMessages pattern): prevents the
    // subscription from tearing down and re-subscribing when the parent
    // conversation snapshot reassigns selectedConversation to a new object
    // reference with the same id.
    //
    // `messageSubGeneration` is included so the self-healing watcher above
    // can force a teardown + re-attach when it detects the listener has
    // fallen behind the conversations subscription (see comment on that
    // effect for the full rationale).
  }, [selectedConversation?.id, currentUser?.uid, confirmedConvoIds, messageSubGeneration]);
  // Synchronously scroll to bottom before paint, then reveal the panel.
  // Runs in the same frame as the first messages render, so the user never
  // sees an unscrolled state — the panel appears already positioned.
  //
  // IMPORTANT: Reveal the panel even when the conversation has zero messages
  // (newly created conversations from Find Teachers → teacher profile arrive
  // empty on first snapshot). Without this, messagesLoading is stuck on true,
  // the message container's visibility:hidden keeps the thread blank, and
  // even after the student sends the first message the optimistic bubble
  // can't render because the whole container is hidden. We still only
  // scroll-anchor when there is content to scroll to.
  useLayoutEffect(() => {
    if (!messagesLoading) return;
    if (!selectedConversation) return;
    if (messages.length > 0 && messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      updateScrollPosition();
    }
    setMessagesLoading(false);
  }, [messages, messagesLoading, selectedConversation]);
  // More consistent timestamp normalization with better fallbacks
  const getTimestampValue = (timestamp: any): number => {
    try {
      // For Firestore timestamps with toDate method
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        try {
          return timestamp.toDate().getTime();
        } catch (e) {
          console.error('Error converting Firestore timestamp:', e);
          // Use a consistent fallback - beginning of today instead of current time
          return new Date().setHours(0, 0, 0, 0);
        }
      }
      // For Date objects
      if (timestamp instanceof Date) {
        return timestamp.getTime();
      }
      // For numeric timestamps (e.g., from optimistic messages using Date.now())
      if (typeof timestamp === 'number') {
        return timestamp;
      }
      // For "Just now" string from optimistic messages - use current time
      if (timestamp === 'Just now') {
        return Date.now();
      }
      // For other string timestamps, try to parse
      if (typeof timestamp === 'string') {
        // Try to extract time from strings like "2:30 PM"
        const timeMatch = timestamp.match(/(\d+):(\d+)\s*([AP]M)?/i);
        if (timeMatch) {
          const now = new Date();
          let hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          const period = timeMatch[3]?.toUpperCase();
          // Convert to 24-hour format
          if (period === 'PM' && hours < 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          // Set the time but keep today's date
          now.setHours(hours, minutes, 0, 0);
          return now.getTime();
        }
        // Try to parse as a date string
        const parsedDate = new Date(timestamp);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getTime();
        }
      }
      // Default fallback - use a fixed reference point instead of current time
      // This ensures stable sorting even with unparseable timestamps
      return new Date().setHours(0, 0, 0, 0); // beginning of today
    } catch (error) {
      console.error('Error in getTimestampValue:', error);
      // Ultimate fallback - use beginning of today as reference
      return new Date().setHours(0, 0, 0, 0);
    }
  };
  // More reliable message sorting with better error handling
  const sortMessagesByTimestamp = (messages: Message[]): Message[] => {
    try {
      return [...messages].sort((a, b) => {
        try {
          // Get numeric values for comparison
          const timeA = getTimestampValue(a.timestamp);
          const timeB = getTimestampValue(b.timestamp);
          // Sort by timestamp
          return timeA - timeB;
        } catch (error) {
          console.error('Error comparing messages:', error);
          // Return 0 to keep relative order in case of error
          return 0;
        }
      });
    } catch (error) {
      console.error('Error sorting messages:', error);
      // Return original array if sorting fails
      return [...messages];
    }
  };
  // Track scroll position to detect if user has manually scrolled.
  // hasManuallyScrolled is derived directly from whether the user is near the
  // bottom — the moment they scroll away from the bottom they are "manually
  // scrolled", and the moment they scroll back near the bottom we resume
  // auto-follow. This matches WhatsApp / Slack / iMessage behavior.
  const updateScrollPosition = () => {
    if (messageContainerRef.current) {
      const container = messageContainerRef.current;
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = container;
      // Consider the user at the bottom if they're within 100px of the bottom
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      // Update the ref with current values
      userScrollPositionRef.current = {
        scrollTop,
        scrollHeight,
        clientHeight,
        isNearBottom,
        // User is "manually scrolled" iff they are NOT near the bottom.
        hasManuallyScrolled: !isNearBottom
      };
    }
  };
  // Single synchronous scroll — no rAF, no setTimeout, no races.
  // The useLayoutEffect keyed on messages.length handles message-append scroll.
  // This function is retained for explicit/imperative scrolls (image onLoad,
  // input focus on mobile, orientation change, first-snapshot reveal).
  const scrollToBottom = (force = false) => {
    if (!messageContainerRef.current || !isMountedRef.current) return;
    // Don't scroll if user has manually scrolled up, unless forced
    if (!force && userScrollPositionRef.current.hasManuallyScrolled) return;
    messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    updateScrollPosition();
    if (force) {
      userScrollPositionRef.current.hasManuallyScrolled = false;
    }
  };
  // Handle conversation selection
  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    activeConversationIdRef.current = conversation.id;
    // Reset message count when selecting a new conversation
    lastMessageCountRef.current = 0;
    setMessagesLoading(true); // Show loading state until first snapshot arrives
    // NEW: Reset scroll tracking
    userScrollPositionRef.current = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      isNearBottom: true,
      hasManuallyScrolled: false
    };
    // On mobile, hide the conversation list
    if (isMobileView) {
      setShowConversationList(false);
    }
    // Mark messages as read
    markConversationOpened(conversation.id, currentUser?.uid, 'student').catch(err => console.error('Error marking conversation as opened:', err));
  };
  // NEW: Completely revised message sending with better scroll coordination
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile || !selectedConversation || !currentUser || isSending) {
      return;
    }
    try {
      setIsSending(true);
      // Always force scroll after sending a message
      shouldForceScrollRef.current = true;
      // Reset manual scroll tracking - user wants to see their sent message
      userScrollPositionRef.current.hasManuallyScrolled = false;
      // Reset typing state
      setMobileState(prev => ({
        ...prev,
        isTyping: false
      }));
      // Create a local copy of the message text before clearing it
      const messageText = newMessage.trim() || undefined;
      const conversationId = selectedConversation.id;
      const senderId = currentUser.uid;
      const receiverId = selectedConversation.teacherId;
      // Log message details for debugging
      console.log('Preparing to send message:', {
        conversationId,
        senderId,
        receiverId,
        messageText: messageText || '(empty)',
        hasAttachment: !!selectedFile
      });
      // Verify we have all required fields
      if (!conversationId) {
        console.error('Missing conversationId for message send');
        throw new Error('Missing conversation ID');
      }
      if (!senderId) {
        console.error('Missing senderId for message send');
        throw new Error('Missing sender ID');
      }
      if (!receiverId) {
        console.error('Missing receiverId for message send');
        throw new Error('Missing receiver ID');
      }
      // Clear the input immediately for better UX
      setNewMessage('');
      // Build optimistic message BEFORE awaiting send — gives the user an
      // immediate bubble so the conversation never appears frozen.
      const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      let optimisticAttachmentUrl: string | undefined;
      let optimisticAttachmentType: 'image' | 'pdf' | 'audio' | 'video' | 'doc' | undefined;
      let optimisticAttachmentName: string | undefined;
      if (selectedFile) {
        optimisticAttachmentUrl = URL.createObjectURL(selectedFile);
        optimisticObjectUrlsRef.current.set(tempId, optimisticAttachmentUrl);
        optimisticAttachmentType = selectedFile.type.startsWith('image/') ? 'image' : selectedFile.type.includes('pdf') ? 'pdf' : selectedFile.type.includes('audio') ? 'audio' : selectedFile.type.includes('video') ? 'video' : 'doc';
        optimisticAttachmentName = selectedFile.name;
      }
      const optimisticMsg: OptimisticMessage = {
        id: tempId,
        senderId,
        receiverId,
        conversationId,
        text: messageText || undefined,
        timestamp: Date.now(),
        read: false,
        attachmentUrl: optimisticAttachmentUrl,
        attachmentType: optimisticAttachmentType,
        attachmentName: optimisticAttachmentName,
        pending: true
      };
      setOptimisticMessages(prev => [...prev, optimisticMsg]);
      // Force scroll so the user sees the new bubble
      requestAnimationFrame(() => {
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
      });
      // Server-authoritative send: no temp bubble. The Firestore snapshot
      // listener will deliver the real message into `messages` once it lands,
      // and the scroll-anchor useLayoutEffect will scroll on append.
      // NEW: Better keyboard handling on mobile
      if (isMobileView) {
        // Don't blur input on iOS as it causes visual glitches
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (!isIOS && messageInputRef.current) {
          messageInputRef.current.blur();
        }
      }
      let attachment = undefined;
      if (selectedFile) {
        // Determine file type
        const fileType = selectedFile.type.startsWith('image/') ? 'image' : selectedFile.type.includes('pdf') ? 'pdf' : selectedFile.type.includes('audio') ? 'audio' : selectedFile.type.includes('video') ? 'video' : 'doc';
        attachment = {
          file: selectedFile,
          type: fileType as 'image' | 'pdf' | 'audio' | 'video' | 'doc'
        };
        console.log('Attachment details:', {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileSize: selectedFile.size,
          mappedType: fileType
        });
      }
      // Scroll is now handled by the useLayoutEffect keyed on messages.length —
      // it runs synchronously before paint, so no setTimeout race is needed here.
      // Send the message to Firestore
      console.log('Calling sendMessage with params:', {
        conversationId,
        senderId,
        receiverId,
        messageText: messageText || '(empty)',
        hasAttachment: !!attachment
      });
      try {
        const messageId = await sendMessage(conversationId, senderId, receiverId, messageText, attachment);
        console.log(`Message sent successfully with ID: ${messageId}`);
        // CRITICAL: Flip the optimistic message out of the "Uploading…" state
        // the moment Firestore acknowledges the write. We do NOT wait for the
        // snapshot listener to re-deliver this message and dedup it — that
        // path can race, fall behind, or miss the snapshot entirely on
        // brand-new conversations, leaving the bubble permanently stuck on
        // "Uploading…" even though delivery already succeeded.
        //
        // We also stamp the optimistic with `serverMessageId` so the snapshot
        // dedup pass below can match by stable server id instead of relying
        // only on text+senderId+attachmentName content matching.
        if (isMountedRef.current) {
          setOptimisticMessages(prev => prev.map(m => m.id === tempId ? {
            ...m,
            pending: false,
            serverMessageId: messageId
          } : m));
        }
        // Clear file selection — the bubble will appear from the snapshot listener.
        setSelectedFile(null);
        setFilePreview(null);
        // Clear any previous errors
        if (error) setError('');
      } catch (sendError) {
        console.error('Error in sendMessage function:', sendError);
        // Mark optimistic message as failed so the user sees the error state.
        setOptimisticMessages(prev => prev.map(m => m.id === tempId ? {
          ...m,
          pending: false,
          failed: true
        } : m));
        if (isMountedRef.current) {
          setError(`Failed to send message: ${sendError.message || 'Unknown error'}. Please try again.`);
        }
        throw sendError; // Re-throw to be caught by the outer catch
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // More detailed error logging
      if (err instanceof Error) {
        console.error('Error details:', err.message);
        console.error('Error stack:', err.stack);
      }
      if (isMountedRef.current) {
        // Show a more specific error message
        setError(err instanceof Error ? `Failed to send message: ${err.message}` : 'Failed to send message. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSending(false);
        // Scroll is handled by useLayoutEffect on messages.length growth.
        // Image attachments additionally trigger scrollToBottom on <img onLoad>.
      }
    }
  };
  // NEW: Improved input change handler with better typing detection
  const handleMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    // Only update typing state when it changes
    const isCurrentlyTyping = value.trim() !== '';
    if (isCurrentlyTyping !== mobileState.isTyping) {
      setMobileState(prev => ({
        ...prev,
        isTyping: isCurrentlyTyping
      }));
    }
  };
  // NEW: Improved keyboard handling for mobile
  const handleInputFocus = () => {
    if (isMobileView) {
      // Update keyboard state
      setMobileState(prev => ({
        ...prev,
        isKeyboardOpen: true
      }));
      // Reset manual scroll tracking when keyboard opens
      userScrollPositionRef.current.hasManuallyScrolled = false;
      // Scroll to bottom with a delay to account for keyboard animation
      // Different delays for iOS vs Android
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const scrollDelay = isIOS ? 300 : 250;
      setTimeout(() => {
        if (isMountedRef.current) {
          scrollToBottom(true);
        }
      }, scrollDelay);
    }
  };
  // NEW: Improved keyboard handling for when input loses focus
  const handleInputBlur = () => {
    if (isMobileView) {
      // Use a short delay to ensure other actions complete first
      setTimeout(() => {
        if (isMountedRef.current) {
          setMobileState(prev => ({
            ...prev,
            isKeyboardOpen: false,
            // Only keep typing state if there's text
            isTyping: newMessage.trim() !== ''
          }));
        }
      }, 100);
    }
  };
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Check file size for mobile (5MB limit for mobile vs 10MB for desktop)
      const maxSize = isMobileView ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const sizeInMB = maxSize / (1024 * 1024);
        alert(`File size exceeds ${sizeInMB}MB limit on ${isMobileView ? 'mobile' : 'desktop'}`);
        return;
      }
      setSelectedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          if (isMountedRef.current) {
            setFilePreview(e.target?.result as string);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };
  // Handle file removal
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Handle back button on mobile
  const handleBackToList = () => {
    setShowConversationList(true);
  };
  // More reliable timestamp formatting
  const formatTime = (timestamp: any) => {
    try {
      // Handle string timestamps
      if (typeof timestamp === 'string') {
        // If it's already formatted (like "2:30 PM"), return it directly
        if (/\d+:\d+\s*([AP]M)?/i.test(timestamp)) {
          return timestamp;
        }
        // Otherwise try to parse it
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        // If parsing fails, return as is
        return timestamp;
      }
      // Handle numeric timestamps (from optimistic messages)
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // Handle Firestore timestamps
      if (timestamp && typeof timestamp.toDate === 'function') {
        try {
          return timestamp.toDate().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          console.error('Error formatting Firestore timestamp:', e);
          return 'Just now';
        }
      }
      // Default fallback
      return 'Just now';
    } catch (error) {
      console.error('Error in formatTime:', error);
      return 'Just now';
    }
  };
  // Check if the user is a student
  useEffect(() => {
    const checkUserType = async () => {
      if (currentUser) {
        // Here you would typically check if the user is a student
        // For now, we'll assume they are
      }
    };
    checkUserType();
  }, [currentUser]);
  // Single deterministic scroll: when messages.length grows AND the user was
  // at the bottom (or a send forced it), set scrollTop synchronously before
  // paint. One write, zero races. This is the ONLY message-append scroll path;
  // it replaces all prior setTimeout/rAF scroll attempts inside
  // handleSendMessage and the onSnapshot subsequent-snapshot branch.
  // Uses its own length tracker (prevRenderedMessagesLengthRef) so it stays
  // independent of lastMessageCountRef, which the onSnapshot callback owns.
  const prevRenderedMessagesLengthRef = useRef(0);
  useLayoutEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;
    const prevLen = prevRenderedMessagesLengthRef.current;
    prevRenderedMessagesLengthRef.current = messages.length;
    if (messages.length === 0) return;
    const grew = messages.length > prevLen;
    if (!grew) return;
    // Industry-standard auto-scroll rule: only auto-scroll on new messages if
    // the user explicitly forced it (they just sent a message) OR they are
    // currently near the bottom. If they scrolled up to read older messages,
    // their position MUST be preserved.
    const shouldScroll = shouldForceScrollRef.current || userScrollPositionRef.current.isNearBottom;
    if (!shouldScroll) return;
    container.scrollTop = container.scrollHeight;
    updateScrollPosition();
    shouldForceScrollRef.current = false;
  }, [messages.length]);
  // NEW: Handle mobile orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      if (isMobileView) {
        // Update viewport height
        setMobileState(prev => ({
          ...prev,
          viewportHeight: window.innerHeight
        }));
        // Scroll to bottom after orientation change completes
        setTimeout(() => {
          if (isMountedRef.current && selectedConversation) {
            scrollToBottom(true);
          }
        }, 300);
      }
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobileView, selectedConversation]);
  // Close 3-dot menu when clicking outside
  useEffect(() => {
    if (!menuOpenForId) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-conv-menu]')) {
        setMenuOpenForId(null);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [menuOpenForId]);
  // Split conversations into active / archived buckets for the student
  const activeConversations = conversations.filter(c => c.archivedForStudent !== true);
  const archivedConversations = conversations.filter(c => c.archivedForStudent === true);
  // Clear selected conversation when it leaves the visible bucket.
  //
  // IMPORTANT: This effect must NOT tear down `selectedConversation` based on
  // transient / incomplete `conversations` snapshots — that was the root cause
  // of the bug where teacher replies failed to appear in the open student
  // thread. Concrete failure mode this guards against:
  //
  //   1. Student creates a new conversation from a teacher profile.
  //      `createConversation` writes `lastMessageTimestamp: serverTimestamp()`
  //      which is a sentinel that resolves server-side.
  //   2. `subscribeToUserConversations` fires before the sentinel resolves.
  //      Its query has `orderBy('lastMessageTimestamp', 'desc')`, which
  //      transiently excludes documents whose timestamp is still pending.
  //      The student sees `conversations === []` for one frame.
  //   3. Without these guards, this effect would treat the new conversation as
  //      "no longer visible," null out `selectedConversation` and
  //      `activeConversationIdRef`, clear `messages`, and unmount the active
  //      message subscription — leaving the student's optimistic first bubble
  //      stuck on "Uploading…" forever and the teacher's reply invisible
  //      until the conversation is manually re-selected.
  //
  // Resolution rules:
  //  - Never tear down while we're still in the initial `loading` state, or
  //    while `conversations` is empty (no authoritative data yet).
  //  - Only tear down when the selected conversation is GENUINELY missing
  //    from BOTH buckets (truly deleted), OR when the user has toggled to a
  //    bucket the conversation doesn't belong in. A conversation that exists
  //    in `conversations` but lives in the OTHER bucket is a legitimate
  //    "leave the visible bucket" case (e.g. user toggled to Archived but
  //    the selection is in Inbox) and is still cleared.
  useEffect(() => {
    if (!selectedConversation) return;
    // Don't touch selection while we don't have authoritative conversations
    // data — this is the window where serverTimestamp-pending convos are
    // briefly missing from the snapshot.
    if (loading) return;
    if (conversations.length === 0) return;
    const inActive = activeConversations.some(c => c.id === selectedConversation.id);
    const inArchived = archivedConversations.some(c => c.id === selectedConversation.id);
    // Conversation still exists somewhere — only clear if the CURRENT bucket
    // doesn't contain it (user toggled views). Don't clear just because the
    // subscription churned.
    if (inActive || inArchived) {
      const inVisibleBucket = archivedView ? inArchived : inActive;
      if (inVisibleBucket) return;
      // Falls through to clear: conversation exists but user is on the
      // wrong bucket.
    }
    setSelectedConversation(null);
    activeConversationIdRef.current = null;
    setMessages([]);
    if (isMobileView) {
      setShowConversationList(true);
    }
  }, [archivedView, conversations, selectedConversation?.id, loading]);
  // Archive handler
  const handleArchiveConversation = async (conversationId: string) => {
    if (!currentUser?.uid || actioningId) return;
    setActioningId(conversationId);
    setMenuOpenForId(null);
    try {
      await archiveConversation(conversationId, currentUser.uid, 'student');
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        activeConversationIdRef.current = null;
        setMessages([]);
        if (isMobileView) {
          setShowConversationList(true);
        }
      }
    } catch (err) {
      console.error('[StudentMessages] Failed to archive conversation:', err);
      alert('Failed to archive conversation. Please try again.');
    } finally {
      setActioningId(null);
    }
  };
  // Unarchive handler
  const handleUnarchiveConversation = async (conversationId: string) => {
    if (!currentUser?.uid || actioningId) return;
    setActioningId(conversationId);
    setMenuOpenForId(null);
    try {
      await unarchiveConversation(conversationId, currentUser.uid, 'student');
    } catch (err) {
      console.error('[StudentMessages] Failed to unarchive conversation:', err);
      alert('Failed to unarchive conversation. Please try again.');
    } finally {
      setActioningId(null);
    }
  };
  // Compute the bucket currently visible in the list
  const visibleBucket = archivedView ? archivedConversations : activeConversations;
  // Redirect if not logged in — wait for auth to finish loading first
  if (authLoading) {
    return null;
  }
  if (!currentUser) {
    navigate('/student-login');
    return null;
  }
  // Define text color classes based on theme
  const textColorClass = isDark ? 'text-white' : 'text-gray-800';
  const subtextColorClass = isDark ? 'text-gray-300' : 'text-gray-600';
  const lightTextColorClass = isDark ? 'text-gray-300' : 'text-gray-500';
  const extraLightTextColorClass = isDark ? 'text-gray-400' : 'text-gray-500';
  // Define background classes based on theme
  const mainBgClass = 'bg-transparent';
  const containerBgClass = isDark ? 'bg-white/10' : 'bg-white';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const conversationItemHoverClass = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100';
  const conversationItemSelectedClass = isDark ? 'bg-white/10' : 'bg-gray-100';
  const emptyStateBgClass = isDark ? 'bg-white/5' : 'bg-gray-50';
  const inputBgClass = isDark ? 'bg-white/5' : 'bg-white';
  const inputBorderClass = isDark ? 'border-white/10' : 'border-gray-300';
  const inputTextClass = isDark ? 'text-white' : 'text-gray-800';
  const iconClass = isDark ? 'text-gray-400' : 'text-gray-500';
  const iconHoverClass = isDark ? 'hover:text-white' : 'hover:text-gray-800';
  const filePreviewBgClass = isDark ? 'bg-white/5' : 'bg-gray-100';
  const fileIconBgClass = isDark ? 'bg-white/10' : 'bg-gray-200';
  const errorBgClass = isDark ? 'bg-red-500/20' : 'bg-red-100';
  const errorBorderClass = isDark ? 'border-red-500/30' : 'border-red-200';
  const errorTextClass = isDark ? 'text-white' : 'text-red-800';
  const avatarBgClass = isDark ? 'bg-white/10' : 'bg-gray-200';
  const messageBubbleFromOtherClass = isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-800';
  const messageBubbleTimeClass = isDark ? 'text-white/70' : 'text-gray-500';
  return <div className={`flex flex-col ${mainBgClass} ${textColorClass} font-sans transition-colors duration-300 overflow-x-hidden w-full max-w-full`} style={{
    height: 'calc(100svh - 80px)',
    overscrollBehaviorX: 'none'
  }} data-id="element-7287">
      <div className="flex items-center justify-between mb-4 shrink-0 max-w-7xl w-full mx-auto px-2" data-id="element-7288">
        <h1 className={`text-3xl md:text-4xl font-serif font-bold ${textColorClass}`} data-id="element-7289">
          Messages
        </h1>
      </div>
      {loading ? <div className="flex-1" aria-hidden="true" data-id="element-7290" /> : error ? <div className="max-w-7xl w-full mx-auto px-2 shrink-0" data-id="element-7291">
          <div className={`${errorBgClass} border ${errorBorderClass} rounded-lg p-4 mb-4`} data-id="element-7292">
            <p className={errorTextClass} data-id="element-7293">{error}</p>
          </div>
        </div> : <div className={`flex-1 min-h-0 max-w-7xl w-full mx-auto px-2 ${containerBgClass} backdrop-blur-sm rounded-xl overflow-hidden border ${borderClass}`} data-id="element-7294">
          <div className="flex h-full" data-id="element-7295">
            {/* Conversation List - only shown on desktop or when showConversationList is true on mobile */}
            {(!isMobileView || showConversationList) && <div className={`w-full md:w-1/3 border-r ${borderClass} overflow-y-auto`} data-id="element-7296">
                <div className={`p-4 border-b ${borderClass}`} data-id="element-7297">
                  <h2 className={`text-lg font-medium ${textColorClass}`} data-id="element-7298">
                    Conversations
                  </h2>
                  {/* Inbox / Archived tabs */}
                  <div className={`mt-3 flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} data-id="element-7299">
                    <button type="button" onClick={() => {
                setArchivedView(false);
                setMenuOpenForId(null);
              }} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${!archivedView ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7300">
                      <InboxIcon className="h-3.5 w-3.5" data-id="element-7301" />
                      Inbox
                      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${!archivedView ? isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600' : isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-200 text-gray-500'}`} data-id="element-7302">
                        {activeConversations.length}
                      </span>
                    </button>
                    <button type="button" onClick={() => {
                setArchivedView(true);
                setMenuOpenForId(null);
              }} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${archivedView ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7303">
                      <ArchiveIcon className="h-3.5 w-3.5" data-id="element-7304" />
                      Archived
                      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${archivedView ? isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600' : isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-200 text-gray-500'}`} data-id="element-7305">
                        {archivedConversations.length}
                      </span>
                    </button>
                  </div>
                </div>
                {visibleBucket.length === 0 ? <div className="p-6 text-center" data-id="element-7306">
                    {archivedView ? <>
                        <p className={lightTextColorClass} data-id="element-7307">
                          No archived conversations
                        </p>
                        <p className={`text-sm ${extraLightTextColorClass} mt-2`} data-id="element-7308">
                          Conversations you archive will appear here. They stay
                          safely stored and can be restored anytime.
                        </p>
                      </> : <>
                        <p className={lightTextColorClass} data-id="element-7309">
                          No conversations yet
                        </p>
                        <p className={`text-sm ${extraLightTextColorClass} mt-2`} data-id="element-7310">
                          Find a teacher to start a conversation
                        </p>
                      </>}
                  </div> : <ul className="px-2 py-2 space-y-1" data-id="element-7311">
                    {visibleBucket.map(conversation => <li key={conversation.id} className={`relative rounded-xl cursor-pointer transition-colors ${selectedConversation?.id === conversation.id ? conversationItemSelectedClass : conversationItemHoverClass}`} onClick={() => handleSelectConversation(conversation)} data-id="element-7312">
                        <div className="px-3 py-3 flex items-center" data-id="element-7313">
                          <div className={`h-12 w-12 rounded-full overflow-hidden ${avatarBgClass} flex-shrink-0 mr-3`} data-id="element-7314">
                            {(() => {
                    const avatarSrc = resolveTeacherAvatar(conversation);
                    const displayName = resolveTeacherDisplayName(conversation);
                    return avatarSrc ? <img src={avatarSrc} alt={displayName} className="h-full w-full object-cover" data-id="element-7315" /> : <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-7316">
                                  <UserIcon className={`h-6 w-6 ${iconClass}`} data-id="element-7317" />
                                </div>;
                  })()}
                          </div>
                          <div className="flex-1 min-w-0 pr-6" data-id="element-7318">
                            <div className="flex justify-between items-center mb-1" data-id="element-7319">
                              <h3 className={`${textColorClass} font-medium truncate`} data-id="element-7320">
                                {resolveTeacherDisplayName(conversation)}
                              </h3>
                              <span className={`text-xs ${extraLightTextColorClass}`} data-id="element-7321">
                                {conversation.lastMessageTimestamp ? formatTime(conversation.lastMessageTimestamp) : ''}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-7322">
                              <p className={`text-sm ${lightTextColorClass} truncate`} data-id="element-7323">
                                {conversation.lastMessage || 'Start a conversation'}
                              </p>
                              {(() => {
                      // Derive unread state from the new
                      // per-participant counter, with a backward-
                      // compat fallback to the legacy `unread`
                      // boolean for conversations created before
                      // the counter rollout.
                      const studentUnreadCount = typeof conversation.unreadForStudent === 'number' ? conversation.unreadForStudent : 0;
                      const senderIsMe = conversation.lastMessageSenderId === currentUser?.uid;
                      const legacyUnread = conversation.unread === true && !senderIsMe;
                      const showPill = studentUnreadCount > 0;
                      const showDot = !showPill && legacyUnread;
                      if (showPill) {
                        const display = studentUnreadCount > 9 ? '9+' : String(studentUnreadCount);
                        return <span aria-label={`${studentUnreadCount} unread ${studentUnreadCount === 1 ? 'message' : 'messages'}`} className="ml-2 flex-shrink-0 min-w-[20px] h-5 px-1.5 text-[11px] font-semibold text-white bg-deep-pink rounded-full flex items-center justify-center" data-id="element-7324">
                                      {display}
                                    </span>;
                      }
                      if (showDot) {
                        return <span className="ml-2 h-2 w-2 bg-deep-pink rounded-full flex-shrink-0" data-id="element-7325"></span>;
                      }
                      return null;
                    })()}
                            </div>
                          </div>
                        </div>
                        {/* 3-dot menu */}
                        <div data-conv-menu className="absolute top-2 right-2" data-id="element-7326">
                          <button type="button" onClick={e => {
                  e.stopPropagation();
                  setMenuOpenForId(menuOpenForId === conversation.id ? null : conversation.id);
                }} aria-label="Conversation options" disabled={actioningId === conversation.id} className={`p-1 rounded-md transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'} ${actioningId === conversation.id ? 'opacity-50' : ''}`} data-id="element-7327">
                            <MoreVerticalIcon className="h-4 w-4" data-id="element-7328" />
                          </button>
                          {menuOpenForId === conversation.id && <div onClick={e => e.stopPropagation()} className={`absolute right-0 top-6 z-20 whitespace-nowrap rounded-md shadow-md ring-1 ${isDark ? 'bg-navy-800 ring-white/10' : 'bg-white ring-black/5'} p-0.5`} data-id="element-7329">
                              {archivedView ? <button type="button" onClick={e => {
                    e.stopPropagation();
                    handleUnarchiveConversation(conversation.id);
                  }} className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-left rounded-[5px] transition-colors ${isDark ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-7330">
                                  <ArchiveRestoreIcon className="h-3.5 w-3.5" data-id="element-7331" />
                                  Unarchive
                                </button> : <button type="button" onClick={e => {
                    e.stopPropagation();
                    handleArchiveConversation(conversation.id);
                  }} className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-left rounded-[5px] transition-colors ${isDark ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-7332">
                                  <ArchiveIcon className="h-3.5 w-3.5" data-id="element-7333" />
                                  Archive
                                </button>}
                            </div>}
                        </div>
                      </li>)}
                  </ul>}
              </div>}
            {/* Message Area - only shown on desktop or when showConversationList is false on mobile */}
            {(!isMobileView || !showConversationList) && <div className="w-full md:w-2/3 flex flex-col min-h-0" data-id="element-7334">
                {selectedConversation ? <>
                    {/* Conversation Header */}
                    <div className={`p-4 border-b ${borderClass} flex items-center shrink-0`} data-id="element-7335">
                      {isMobileView && <button onClick={handleBackToList} className={`mr-2 ${iconClass} ${iconHoverClass}`} data-id="element-7336">
                          <ChevronLeftIcon className="h-5 w-5" data-id="element-7337" />
                        </button>}
                      <div className={`h-10 w-10 rounded-full overflow-hidden ${avatarBgClass} mr-3`} data-id="element-7338">
                        {(() => {
                  const avatarSrc = resolveTeacherAvatar(selectedConversation);
                  const displayName = resolveTeacherDisplayName(selectedConversation);
                  return avatarSrc ? <img src={avatarSrc} alt={displayName} className="h-full w-full object-cover" data-id="element-7339" /> : <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-7340">
                              <UserIcon className={`h-5 w-5 ${iconClass}`} data-id="element-7341" />
                            </div>;
                })()}
                      </div>
                      <div className="flex-1" data-id="element-7342">
                        <h3 className={`${textColorClass} font-medium`} data-id="element-7343">
                          {resolveTeacherDisplayName(selectedConversation)}
                        </h3>
                        <p className={`text-xs ${extraLightTextColorClass}`} data-id="element-7344">
                          {resolveTeacherSubtitle(selectedConversation)}
                        </p>
                      </div>
                    </div>
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 min-h-0 w-full max-w-full" style={{
              visibility: messagesLoading ? 'hidden' : 'visible',
              overscrollBehaviorX: 'none',
              WebkitOverflowScrolling: 'touch'
            }} ref={messageContainerRef} data-id="element-7345">
                      {messagesLoading ? null : messages.length === 0 && optimisticMessages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-7346">
                          <div className={`${emptyStateBgClass} rounded-full p-4 mb-3`} data-id="element-7347">
                            <MessageSquareIcon className={`h-8 w-8 ${iconClass}`} data-id="element-7348" />
                          </div>
                          <h3 className={`${textColorClass} font-medium mb-1`} data-id="element-7349">
                            Start a conversation
                          </h3>
                          <p className={`text-sm ${extraLightTextColorClass} max-w-xs`} data-id="element-7350">
                            Send a message to{' '}
                            {resolveTeacherDisplayName(selectedConversation)} to
                            discuss lessons or ask questions.
                          </p>
                        </div> : (() => {
                // Combine real + optimistic, sorted by timestamp
                const toMs = (t: any): number => {
                  if (t && typeof t === 'object' && typeof t.toDate === 'function') {
                    try {
                      return t.toDate().getTime();
                    } catch {
                      return 0;
                    }
                  }
                  if (t instanceof Date) return t.getTime();
                  if (typeof t === 'number') return t;
                  return 0;
                };
                const combined: OptimisticMessage[] = [...messages, ...optimisticMessages].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));
                return combined.map(message => {
                  const isFromCurrentUser = message.senderId === currentUser.uid;
                  const isPending = (message as OptimisticMessage).pending === true;
                  const isFailed = (message as OptimisticMessage).failed === true;
                  return <div key={message.id} className={`flex ${isFromCurrentUser ? 'justify-end' : 'justify-start'}`} data-id="element-7351">
                                <div className={`relative max-w-[75%] rounded-lg px-4 py-2 break-words [overflow-wrap:anywhere] ${isFromCurrentUser ? 'bg-blue-500 text-white' : messageBubbleFromOtherClass} ${isPending ? 'opacity-70' : ''} ${isFailed ? 'ring-2 ring-red-400' : ''} transition-opacity duration-200`} data-id="element-7352">
                                  {message.text && <p data-id="element-7353">{message.text}</p>}
                                  {message.attachmentUrl && <div className="mt-2" data-id="element-7354">
                                      {message.attachmentType === 'image' ? <div className="mt-1 w-full max-w-[240px] aspect-[4/3] rounded overflow-hidden bg-black/5 dark:bg-white/5 flex items-center justify-center" data-id="element-7355">
                                          <img src={message.attachmentUrl} alt="Attachment" className="block w-full h-full max-w-full rounded object-cover cursor-pointer hover:opacity-90 transition-opacity" loading="lazy" decoding="async" onClick={() => setLightboxImage(message.attachmentUrl!)} onLoad={() => {
                            // Only re-anchor to the bottom
                            // if the user is already there.
                            // `force=false` makes this
                            // respect manual scroll, so
                            // lazy-loaded images of the
                            // user's own old messages do
                            // NOT yank them back down while
                            // they're reading history.
                            if (isFromCurrentUser && !messagesLoading) {
                              scrollToBottom(false);
                            }
                          }} data-id="element-7356" />
                                        </div> : <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center ${filePreviewBgClass} rounded p-2 text-sm`} data-id="element-7357">
                                          <FileIcon className="h-4 w-4 mr-2 text-red-500" data-id="element-7358" />
                                          <span className={`truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7359">
                                            {message.attachmentName || 'Attachment'}
                                          </span>
                                        </a>}
                                    </div>}
                                  <div className={`flex items-center justify-end gap-1 text-xs mt-1 ${isFromCurrentUser ? 'text-white/70' : extraLightTextColorClass}`} data-id="element-7360">
                                    {isPending ? <span className="flex items-center gap-1" data-id="element-7361">
                                        <LoaderIcon className="h-3 w-3 animate-spin" data-id="element-7362" />
                                        Uploading…
                                      </span> : isFailed ? <span className="flex items-center gap-1 text-red-200" data-id="element-7363">
                                        <AlertCircleIcon className="h-3 w-3" data-id="element-7364" />
                                        Failed
                                      </span> : formatTime(message.timestamp)}
                                  </div>
                                </div>
                              </div>;
                });
              })()}
                      <div ref={messagesEndRef} data-id="element-7365" />
                    </div>
                    {/* Message Input - Fixed at bottom */}
                    <div className={`p-4 border-t ${borderClass} shrink-0`} data-id="element-7366">
                      {selectedFile && <div className="mb-2" data-id="element-7367">
                          <div className={`inline-flex items-center gap-2 pl-1.5 pr-1 py-1 rounded-full max-w-[260px] sm:max-w-xs ${filePreviewBgClass} border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-7368">
                            {filePreview ? <img src={filePreview} alt="Preview" className="h-7 w-7 rounded-full object-cover flex-shrink-0" data-id="element-7369" /> : <div className={`h-7 w-7 ${fileIconBgClass} rounded-full flex items-center justify-center flex-shrink-0`} data-id="element-7370">
                                <FileIcon className={`h-3.5 w-3.5 ${iconClass}`} data-id="element-7371" />
                              </div>}
                            <span className={`text-xs font-medium truncate min-w-0 ${textColorClass}`} data-id="element-7372">
                              {selectedFile.name}
                            </span>
                            <button type="button" onClick={handleRemoveFile} aria-label="Remove attachment" className={`p-1 rounded-full flex-shrink-0 transition-colors ${iconClass} ${iconHoverClass}`} data-id="element-7373">
                              <XIcon className="h-3.5 w-3.5" data-id="element-7374" />
                            </button>
                          </div>
                        </div>}
                      <form onSubmit={handleSendMessage} className="flex items-center" data-id="element-7375">
                        <button type="button" className={`${iconClass} ${iconHoverClass} mr-2`} onClick={() => fileInputRef.current?.click()} disabled={isSending} data-id="element-7376">
                          <PaperclipIcon className="h-5 w-5" data-id="element-7377" />
                        </button>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.mp3,.mp4" data-id="element-7378" />
                        <input type="text" ref={messageInputRef} value={newMessage} onChange={handleMessageInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} placeholder={isSending ? 'Sending...' : 'Type a message...'} className={`flex-1 ${inputBgClass} border ${inputBorderClass} rounded-lg px-4 py-2 ${inputTextClass} focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400`} data-id="element-7379" />
                        <button type="submit" disabled={!newMessage.trim() && !selectedFile || isSending} className={`ml-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 ${!newMessage.trim() && !selectedFile || isSending ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-7380">
                          <SendIcon className="h-5 w-5" data-id="element-7381" />
                        </button>
                      </form>
                    </div>
                  </> : <div className="h-full flex flex-col items-center justify-center text-center p-6" data-id="element-7382">
                    <div className={`${emptyStateBgClass} rounded-full p-6 mb-4`} data-id="element-7383">
                      <MessageSquareIcon className={`h-10 w-10 ${iconClass}`} data-id="element-7384" />
                    </div>
                    <h3 className={`text-xl font-medium ${textColorClass} mb-2`} data-id="element-7385">
                      Your Messages
                    </h3>
                    <p className={`${subtextColorClass} max-w-md mb-6`} data-id="element-7386">
                      Select a conversation from the list or find a teacher to
                      start messaging.
                    </p>
                    <button onClick={() => navigate('/find-teachers')} className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors" data-id="element-7387">
                      Find Teachers
                    </button>
                  </div>}
              </div>}
          </div>
        </div>}
      {/* Image Lightbox */}
      {lightboxImage && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] animate-in fade-in duration-200" onClick={() => setLightboxImage(null)} onKeyDown={e => {
      if (e.key === 'Escape') setLightboxImage(null);
    }} tabIndex={0} ref={el => el?.focus()} data-id="element-7388">
          <button onClick={e => {
        e.stopPropagation();
        setLightboxImage(null);
      }} className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10" aria-label="Close lightbox" data-id="element-7389">
            <XIcon className="h-8 w-8" data-id="element-7390" />
          </button>
          <img src={lightboxImage} alt="Full size attachment" className="max-w-[90%] max-h-[90%] rounded-lg object-contain" onClick={e => e.stopPropagation()} data-id="element-7391" />
        </div>}
    </div>;
};